import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Calendar, Plus, Search, MoreVertical, Trash2,
  CalendarDays, X, ChevronRight, Eye, ArrowRight,
} from 'lucide-react'
import { Button } from '@components/ui/button'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@components/ui/tabs'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@components/ui/dropdown-menu'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@components/ui/tooltip'
import { useToast } from '@hooks/use-toast'
import { appointmentApi, AppointmentSearchParams } from '@services/appointment.service'
import { Appointment, DoctorOption } from '@/types'
import { CreateAppointmentDialog } from '@components/appointments/CreateAppointmentDialog'
import { PendingApprovalsSection } from '@components/appointments/PendingApprovalsSection'
import { useAuthStore } from '@hooks/useAuthStore'
import { useClinicTimezone } from '@hooks/useClinicTimezone'
import { formatClinicTime, DEFAULT_CLINIC_TZ } from '@lib/clinicDate'
import { cn } from '@lib/utils'

// ─── Constants ───────────────────────────────────────────────────────────────

const appointmentTypeLabels: Record<string, string> = {
  consultation: 'Consulta',
  follow_up: 'Seguimiento',
  emergency: 'Emergencia',
  routine_checkup: 'Revisión Rutinaria',
  vaccination: 'Vacunación',
  lab_test: 'Laboratorio',
  imaging: 'Imagen',
  procedure: 'Procedimiento',
  telemedicine: 'Telemedicina',
}

const statusConfig: Record<string, { bg: string; border: string; text: string; dot: string; label: string }> = {
  pending_approval: { bg: 'bg-amber-50',   border: 'border-amber-300',  text: 'text-amber-700',   dot: 'bg-amber-400',   label: 'Pendiente' },
  scheduled:        { bg: 'bg-blue-50',    border: 'border-blue-300',   text: 'text-blue-700',    dot: 'bg-blue-400',    label: 'Programada' },
  checked_in:       { bg: 'bg-cyan-50',    border: 'border-cyan-300',   text: 'text-cyan-700',    dot: 'bg-cyan-400',    label: 'En Espera' },
  in_progress:      { bg: 'bg-violet-50',  border: 'border-violet-300', text: 'text-violet-700',  dot: 'bg-violet-400',  label: 'En Consulta' },
  completed:        { bg: 'bg-emerald-50', border: 'border-emerald-300',text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Completada' },
  cancelled:        { bg: 'bg-red-50',     border: 'border-red-200',    text: 'text-red-600',     dot: 'bg-red-400',     label: 'Cancelada' },
  no_show:          { bg: 'bg-slate-50',   border: 'border-slate-200',  text: 'text-slate-500',   dot: 'bg-slate-400',   label: 'No Asistió' },
  confirmed:        { bg: 'bg-emerald-50', border: 'border-emerald-300',text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Confirmada' },
  rejected:         { bg: 'bg-red-50',     border: 'border-red-200',    text: 'text-red-600',     dot: 'bg-red-400',     label: 'Rechazada' },
  rescheduled:      { bg: 'bg-orange-50',  border: 'border-orange-300', text: 'text-orange-700',  dot: 'bg-orange-400',  label: 'Reprogramada' },
}

const NEXT_STATUSES: Record<string, string[]> = {
  pending_approval: ['scheduled', 'rejected', 'cancelled'],
  scheduled:        ['confirmed', 'checked_in', 'cancelled', 'no_show', 'rescheduled'],
  confirmed:        ['checked_in', 'in_progress', 'cancelled', 'no_show', 'rescheduled'],
  checked_in:       ['in_progress', 'cancelled', 'no_show', 'rescheduled'],
  in_progress:      ['completed'],
  rescheduled:      ['pending_approval', 'scheduled', 'confirmed', 'checked_in', 'cancelled'],
  completed:        [],
  cancelled:        [],
  no_show:          [],
  rejected:         [],
}

const STATUS_CHANGE_ROLES = ['doctor', 'admin', 'super_admin']
const DELETABLE_STATUSES  = ['cancelled', 'no_show']

const STATUS_FILTER_OPTIONS = [
  'all', 'pending_approval', 'scheduled', 'confirmed', 'checked_in',
  'in_progress', 'completed', 'cancelled', 'no_show', 'rejected', 'rescheduled',
]

type TabKey = 'upcoming' | 'past' | 'all' | 'cancelled'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getPatientName(a: Appointment): string {
  if (a.patient) return `${a.patient.first_name} ${a.patient.last_name}`
  return a.patient_name || 'Paciente'
}

function getDoctorName(a: Appointment): string {
  if (a.doctor) return `Dr. ${a.doctor.first_name} ${a.doctor.last_name}`
  return 'Médico'
}

function toDateKey(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz || DEFAULT_CLINIC_TZ }).format(new Date(iso))
}

function getDayLabel(dateKey: string): string {
  const todayKey    = new Intl.DateTimeFormat('en-CA').format(new Date())
  const tomorrowKey = new Intl.DateTimeFormat('en-CA').format(new Date(Date.now() + 86_400_000))
  if (dateKey === todayKey)    return 'Hoy'
  if (dateKey === tomorrowKey) return 'Mañana'
  const d = new Date(dateKey + 'T12:00:00')
  return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })
}

function isoToday():     string { return new Intl.DateTimeFormat('en-CA').format(new Date()) }
function isoYesterday(): string { return new Intl.DateTimeFormat('en-CA').format(new Date(Date.now() - 86_400_000)) }
function isoTomorrow():  string { return new Intl.DateTimeFormat('en-CA').format(new Date(Date.now() + 86_400_000)) }

// ─── Component ───────────────────────────────────────────────────────────────

export function AppointmentsPage() {
  const { t }    = useTranslation()
  const { toast } = useToast()
  const navigate  = useNavigate()
  const user      = useAuthStore((s) => s.user)
  const clinicTz  = useClinicTimezone(user?.id)
  const tz        = clinicTz || DEFAULT_CLINIC_TZ

  // List state
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [loading, setLoading]           = useState(true)
  const [total, setTotal]               = useState(0)
  const [page, setPage]                 = useState(1)
  const pageSize = 20
  const [refreshKey, setRefreshKey]     = useState(0)
  const refresh = () => setRefreshKey((k) => k + 1)

  // Filters / tabs
  const [activeTab,     setActiveTab]     = useState<TabKey>('upcoming')
  const [statusFilter,  setStatusFilter]  = useState('all')
  const [doctorFilter,  setDoctorFilter]  = useState('all')
  const [dateFrom,      setDateFrom]      = useState('')
  const [dateTo,        setDateTo]        = useState('')
  const [searchQuery,   setSearchQuery]   = useState('')
  const [doctors,       setDoctors]       = useState<DoctorOption[]>([])
  const [stats,         setStats]         = useState({ today: 0, pending: 0, recentCancelled: 0 })
  const [isCreateOpen,  setIsCreateOpen]  = useState(false)

  // Today / Next Appointment section
  const [todayAppts,  setTodayAppts]  = useState<Appointment[]>([])
  const [todayTotal,  setTodayTotal]  = useState(0)
  const [nextAppt,    setNextAppt]    = useState<Appointment | null>(null)
  const [todayLoaded, setTodayLoaded] = useState(false)

  const canChangeStatus = STATUS_CHANGE_ROLES.includes(user?.role ?? '')

  // Fetch doctors once
  useEffect(() => {
    appointmentApi.getDoctors().then((r) => setDoctors(r.items)).catch(() => {})
  }, [])

  // Fetch today's appointments for the top strip (refreshes with refreshKey)
  useEffect(() => {
    const today = isoToday()
    appointmentApi.getAppointments({ date_from: today, date_to: today, limit: 5 })
      .then((r) => {
        setTodayAppts(r.items.slice(0, 4))
        setTodayTotal(r.total)
        setTodayLoaded(true)
        if (r.total === 0) {
          appointmentApi.getAppointments({ date_from: isoTomorrow(), limit: 1 })
            .then((nr) => setNextAppt(nr.items[0] ?? null))
            .catch(() => {})
        } else {
          setNextAppt(null)
        }
      })
      .catch(() => { setTodayLoaded(true) })
  }, [refreshKey])

  // Fetch summary stats
  useEffect(() => {
    const today = isoToday()
    Promise.all([
      appointmentApi.getAppointments({ date_from: today, date_to: today, limit: 1 }),
      appointmentApi.getAppointments({ status: 'pending_approval', limit: 1 }),
      appointmentApi.getAppointments({ status: 'cancelled', date_from: today, limit: 1 }),
    ]).then(([todayRes, pendingRes, cancelledRes]) => {
      setStats({ today: todayRes.total, pending: pendingRes.total, recentCancelled: cancelledRes.total })
    }).catch(() => {})
  }, [refreshKey])

  // Fetch appointments when any filter/tab/page changes
  useEffect(() => {
    const params: AppointmentSearchParams = { skip: (page - 1) * pageSize, limit: pageSize }

    if (activeTab === 'cancelled') {
      params.status = 'cancelled'
    } else {
      if (statusFilter !== 'all') params.status = statusFilter
      if (activeTab === 'upcoming') {
        params.date_from = dateFrom || isoToday()
      } else if (activeTab === 'past') {
        params.date_to = dateTo || isoYesterday()
      } else {
        if (dateFrom) params.date_from = dateFrom
        if (dateTo)   params.date_to   = dateTo
      }
    }
    if (doctorFilter !== 'all') params.doctor_id = doctorFilter

    let cancelled = false
    setLoading(true)
    appointmentApi.getAppointments(params)
      .then((r) => {
        if (!cancelled) { setAppointments(r.items); setTotal(r.total) }
      })
      .catch(() => {
        if (!cancelled) toast({ title: t('common.error'), description: t('appointments.error_loading'), variant: 'destructive' })
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [activeTab, statusFilter, doctorFilter, dateFrom, dateTo, page, refreshKey, t, toast, pageSize])

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab)
    setPage(1)
    setDateFrom('')
    setDateTo('')
    if (tab !== 'cancelled') setStatusFilter('all')
  }

  const clearFilters = () => {
    setDateFrom('')
    setDateTo('')
    setStatusFilter('all')
    setDoctorFilter('all')
    setPage(1)
  }

  const handleStatusChange = async (appointment: Appointment, newStatus: string) => {
    try {
      await appointmentApi.updateAppointment(appointment.id, { status: newStatus })
      toast({ title: t('common.success', 'Éxito'), description: `Cita marcada como ${statusConfig[newStatus]?.label ?? newStatus}.` })
      refresh()
    } catch (error: any) {
      toast({
        title: t('common.error', 'Error'),
        description: error?.response?.data?.detail || 'No se pudo actualizar el estado.',
        variant: 'destructive',
      })
    }
  }

  const handleDelete = async (appointment: Appointment) => {
    if (!window.confirm('¿Eliminar esta cita? Esta acción no se puede deshacer.')) return
    try {
      await appointmentApi.deleteAppointment(appointment.id)
      toast({ title: t('common.success', 'Éxito'), description: 'Cita eliminada.' })
      refresh()
    } catch (error: any) {
      toast({
        title: t('common.error', 'Error'),
        description: error?.response?.data?.detail || 'No se pudo eliminar la cita.',
        variant: 'destructive',
      })
    }
  }

  // Client-side search across current page
  const filteredAppointments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return appointments
    return appointments.filter((a) =>
      a.appointment_number?.toLowerCase().includes(q) ||
      getPatientName(a).toLowerCase().includes(q) ||
      a.appointment_type?.toLowerCase().includes(q) ||
      a.reason?.toLowerCase().includes(q) ||
      getDoctorName(a).toLowerCase().includes(q)
    )
  }, [appointments, searchQuery])

  // Group cards by date
  const groupedAppointments = useMemo(() => {
    const map = new Map<string, Appointment[]>()
    for (const appt of filteredAppointments) {
      const key = toDateKey(appt.appointment_date, tz)
      const group = map.get(key) ?? []
      group.push(appt)
      map.set(key, group)
    }
    const entries = Array.from(map.entries())
    entries.sort(([a], [b]) => (activeTab === 'past' ? b.localeCompare(a) : a.localeCompare(b)))
    return entries
  }, [filteredAppointments, tz, activeTab])

  const totalPages       = Math.max(1, Math.ceil(total / pageSize))
  const hasActiveFilters = !!(dateFrom || dateTo || statusFilter !== 'all' || doctorFilter !== 'all')

  const goToTodayList = () => {
    const today = isoToday()
    setActiveTab('all')
    setDateFrom(today)
    setDateTo(today)
    setPage(1)
  }

  return (
    <TooltipProvider>
      <div className="animate-fade-in space-y-5">

        {/* Doctor-specific pending approvals panel */}
        {user?.role === 'doctor' && user.id && (
          <div className="mb-2">
            <PendingApprovalsSection doctorId={user.id} onResolved={refresh} />
          </div>
        )}

        {/* ── Page header ── */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] text-ml-on-surface-variant/50 font-bold uppercase tracking-widest mb-1.5">
              <span>Sistema</span>
              <ChevronRight className="h-3 w-3" />
              <span className="text-ml-primary">Citas</span>
            </div>
            <h1 className="text-3xl font-headline italic text-ml-primary leading-tight">
              Gestión de Citas
            </h1>
            <p className="text-sm text-ml-on-surface-variant mt-1">
              Gestiona y agenda citas con pacientes
            </p>
          </div>
          <Button
            onClick={() => setIsCreateOpen(true)}
            className="flex-shrink-0 px-5 py-2.5 rounded bg-ml-primary text-white font-bold shadow-md hover:bg-ml-primary/90 transition-all"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nueva Cita
          </Button>
        </div>

        {/* ── Search — prominent, full-width ── */}
        <div className="relative w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ml-on-surface-variant/40 pointer-events-none" />
          <input
            placeholder="Buscar por nombre de paciente, motivo o médico..."
            className="w-full bg-ml-surface-container-low border border-ml-outline-variant/15 rounded-2xl py-3 pl-12 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-ml-primary/20 transition-all"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 text-ml-on-surface-variant/50 hover:text-ml-on-surface transition-colors"
              onClick={() => setSearchQuery('')}
              aria-label="Limpiar búsqueda"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* ── Today / Next Appointment strip ── */}
        {todayLoaded && (
          <div
            className={cn(
              'rounded-2xl border-l-4 border-ml-primary px-5 py-4',
              todayTotal > 0 ? 'bg-ml-primary/5' : 'bg-ml-surface-container-low',
            )}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-widest text-ml-on-surface-variant/60">
                  {todayTotal > 0 ? 'Hoy' : 'Próxima Cita'}
                </span>
                {todayTotal > 0 && (
                  <span className="bg-ml-primary text-white text-[10px] font-bold px-2 py-0.5 rounded leading-none">
                    {todayTotal}
                  </span>
                )}
              </div>
              {todayTotal > 4 && (
                <button
                  onClick={goToTodayList}
                  className="flex items-center gap-1 text-xs text-ml-primary hover:underline font-semibold"
                >
                  Ver todas las de hoy ({todayTotal})
                  <ArrowRight className="h-3 w-3" />
                </button>
              )}
            </div>

            {todayTotal === 0 && !nextAppt ? (
              <p className="text-sm text-ml-on-surface-variant/60">
                No hay citas programadas para hoy ni próximamente.
              </p>
            ) : todayTotal === 0 && nextAppt ? (
              /* Next upcoming appointment card */
              (() => {
                const sc        = statusConfig[nextAppt.status] ?? statusConfig.scheduled
                const apptDate  = new Date(nextAppt.appointment_date)
                const dayNum    = new Intl.DateTimeFormat('es-MX', { timeZone: tz, day: 'numeric' }).format(apptDate)
                const monthAbbr = new Intl.DateTimeFormat('es-MX', { timeZone: tz, month: 'short' }).format(apptDate).replace('.', '').toUpperCase()
                const weekday   = new Intl.DateTimeFormat('es-MX', { timeZone: tz, weekday: 'long' }).format(apptDate)
                const timeStr   = formatClinicTime(nextAppt.appointment_date, tz)
                return (
                  <div className="flex items-center gap-4 bg-white rounded-xl border border-ml-outline-variant/10 px-4 py-3 max-w-sm">
                    <div className="flex-shrink-0 w-14 flex flex-col items-center bg-ml-surface-container-low rounded-xl py-2 px-1 text-center">
                      <span className="text-[9px] font-bold text-ml-on-surface-variant/45 uppercase tracking-widest leading-none">{monthAbbr}</span>
                      <span className="text-2xl font-headline font-bold text-ml-primary leading-tight">{dayNum}</span>
                      <span className="text-[9px] font-semibold text-ml-on-surface-variant/60 tabular-nums leading-none mt-0.5">{timeStr}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-ml-on-surface-variant/50 capitalize mb-0.5">{weekday}</p>
                      <p className="font-bold text-sm text-ml-on-surface truncate">{getPatientName(nextAppt)}</p>
                      <p className="text-xs text-ml-on-surface-variant truncate">{nextAppt.reason || appointmentTypeLabels[nextAppt.appointment_type]}</p>
                    </div>
                    <span className={cn('flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold border', sc.bg, sc.border, sc.text)}>
                      <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', sc.dot)} />
                      {sc.label}
                    </span>
                  </div>
                )
              })()
            ) : (
              /* Today's appointments compact grid */
              <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
                {todayAppts.map((appt) => {
                  const sc      = statusConfig[appt.status] ?? statusConfig.scheduled
                  const timeStr = formatClinicTime(appt.appointment_date, tz)
                  return (
                    <div
                      key={appt.id}
                      className="flex items-center gap-2.5 bg-white rounded-xl border border-ml-outline-variant/10 px-3 py-2.5"
                    >
                      <div className="flex-shrink-0 w-12 text-center">
                        <span className="block text-[11px] font-bold text-ml-primary tabular-nums leading-tight">{timeStr}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-xs text-ml-on-surface truncate">{getPatientName(appt)}</p>
                        <p className="text-[10px] text-ml-on-surface-variant truncate">
                          {appt.reason || appointmentTypeLabels[appt.appointment_type] || ''}
                        </p>
                      </div>
                      <span
                        title={sc.label}
                        className={cn('flex-shrink-0 w-2 h-2 rounded-full', sc.dot)}
                      />
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Summary counters ── */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-ml-surface-container-low rounded-xl px-4 py-2.5">
            <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
            <span className="text-xs text-ml-on-surface-variant">
              <strong className="text-ml-on-surface font-bold">{stats.today}</strong>{' '}
              {stats.today === 1 ? 'cita hoy' : 'citas hoy'}
            </span>
          </div>
          <div className="flex items-center gap-2 bg-ml-surface-container-low rounded-xl px-4 py-2.5">
            <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
            <span className="text-xs text-ml-on-surface-variant">
              <strong className="text-ml-on-surface font-bold">{stats.pending}</strong>{' '}
              {stats.pending === 1 ? 'pendiente de confirmación' : 'pendientes de confirmación'}
            </span>
          </div>
          {stats.recentCancelled > 0 && (
            <div className="flex items-center gap-2 bg-ml-surface-container-low rounded-xl px-4 py-2.5">
              <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
              <span className="text-xs text-ml-on-surface-variant">
                <strong className="text-ml-on-surface font-bold">{stats.recentCancelled}</strong>{' '}
                {stats.recentCancelled === 1 ? 'cancelación hoy' : 'cancelaciones hoy'}
              </span>
            </div>
          )}
        </div>

        {/* ── Tabs + compact secondary filters in one row ── */}
        <div className="flex items-center gap-3 flex-wrap">
          <Tabs value={activeTab} onValueChange={(v) => handleTabChange(v as TabKey)} className="flex-shrink-0">
            <TabsList className="bg-ml-surface-container-low rounded-xl h-auto p-1 gap-0.5">
              {(
                [
                  { key: 'upcoming',  label: 'Próximas' },
                  { key: 'past',      label: 'Pasadas' },
                  { key: 'all',       label: 'Todas' },
                  { key: 'cancelled', label: 'Canceladas' },
                ] as { key: TabKey; label: string }[]
              ).map(({ key, label }) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  className="rounded px-4 py-2 text-sm font-medium text-ml-on-surface-variant data-[state=active]:bg-white data-[state=active]:text-ml-primary data-[state=active]:shadow-sm transition-all"
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {activeTab !== 'cancelled' && (
            <>
              {/* Date from */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5 bg-ml-surface-container-low rounded-xl px-3 py-2 cursor-text">
                    <CalendarDays className="h-3.5 w-3.5 text-ml-on-surface-variant/60 flex-shrink-0 pointer-events-none" />
                    <input
                      type="date"
                      className="bg-transparent border-none text-xs text-ml-on-surface outline-none w-28 cursor-pointer"
                      value={dateFrom}
                      onChange={(e) => { setDateFrom(e.target.value); setPage(1) }}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>Desde</TooltipContent>
              </Tooltip>

              <span className="text-xs text-ml-on-surface-variant/30 font-light">—</span>

              {/* Date to */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5 bg-ml-surface-container-low rounded-xl px-3 py-2 cursor-text">
                    <CalendarDays className="h-3.5 w-3.5 text-ml-on-surface-variant/60 flex-shrink-0 pointer-events-none" />
                    <input
                      type="date"
                      className="bg-transparent border-none text-xs text-ml-on-surface outline-none w-28 cursor-pointer"
                      value={dateTo}
                      onChange={(e) => { setDateTo(e.target.value); setPage(1) }}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>Hasta</TooltipContent>
              </Tooltip>

              {/* Status filter */}
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1) }}>
                <SelectTrigger className="w-40 bg-ml-surface-container-low border-none rounded-xl text-xs focus:ring-1 focus:ring-ml-primary/20">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTER_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s === 'all' ? 'Todos los estados' : (statusConfig[s]?.label ?? s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Doctor filter */}
              {doctors.length > 1 && (
                <Select value={doctorFilter} onValueChange={(v) => { setDoctorFilter(v); setPage(1) }}>
                  <SelectTrigger className="w-48 bg-ml-surface-container-low border-none rounded-xl text-xs focus:ring-1 focus:ring-ml-primary/20">
                    <SelectValue placeholder="Médico" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los médicos</SelectItem>
                    {doctors.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        Dr. {d.first_name} {d.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1.5 text-xs text-ml-on-surface-variant/60 hover:text-ml-primary transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                  Limpiar
                </button>
              )}
            </>
          )}
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ml-primary" />
          </div>
        ) : filteredAppointments.length === 0 ? (
          /* ── Empty state ── */
          <div className="flex flex-col items-center justify-center py-20 text-center bg-ml-surface-container-lowest rounded-2xl border border-ml-outline-variant/10">
            <div className="w-16 h-16 rounded-2xl bg-ml-surface-container-low flex items-center justify-center mb-4">
              <Calendar className="h-8 w-8 text-ml-on-surface-variant/25" />
            </div>
            <h3 className="text-base font-semibold text-ml-on-surface mb-1">
              {searchQuery
                ? `No se encontraron citas para "${searchQuery}"`
                : 'No hay citas en este período'}
            </h3>
            <p className="text-sm text-ml-on-surface-variant mb-5 max-w-xs">
              {searchQuery
                ? 'Intenta con otro nombre o borra la búsqueda.'
                : hasActiveFilters
                ? 'Ninguna cita coincide con los filtros aplicados.'
                : activeTab === 'upcoming'
                ? 'No hay citas próximas programadas.'
                : activeTab === 'past'
                ? 'No hay citas pasadas registradas.'
                : activeTab === 'cancelled'
                ? 'No hay citas canceladas.'
                : 'No hay citas registradas aún.'}
            </p>
            <div className="flex items-center gap-3">
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-sm text-ml-on-surface-variant/70 hover:text-ml-primary underline transition-colors"
                >
                  Limpiar búsqueda
                </button>
              )}
              {hasActiveFilters && !searchQuery && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-ml-on-surface-variant/70 hover:text-ml-primary underline transition-colors"
                >
                  Limpiar filtros
                </button>
              )}
              <Button
                onClick={() => setIsCreateOpen(true)}
                size="sm"
                className="rounded-xl bg-ml-primary text-white font-semibold px-4"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Nueva Cita
              </Button>
            </div>
          </div>
        ) : (
          /* ── Card list grouped by date ── */
          <div className="space-y-7">
            {groupedAppointments.map(([dateKey, appts]) => (
              <div key={dateKey}>
                {/* Day heading */}
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[11px] font-bold text-ml-on-surface-variant/60 uppercase tracking-widest capitalize flex-shrink-0">
                    {getDayLabel(dateKey)}
                  </span>
                  <span className="text-[11px] text-ml-on-surface-variant/35 font-medium flex-shrink-0">
                    {appts.length} cita{appts.length !== 1 ? 's' : ''}
                  </span>
                  <div className="flex-1 h-px bg-ml-outline-variant/10" />
                </div>

                {/* Cards — 12px gap */}
                <div className="space-y-3">
                  {appts.map((appointment) => {
                    const sc           = statusConfig[appointment.status] ?? statusConfig.scheduled
                    const nextStatuses = NEXT_STATUSES[appointment.status] ?? []
                    const isDeletable  = DELETABLE_STATUSES.includes(appointment.status)
                    const patientName  = getPatientName(appointment)
                    const doctorName   = getDoctorName(appointment)
                    const apptDate     = new Date(appointment.appointment_date)
                    const dayNum       = new Intl.DateTimeFormat('es-MX', { timeZone: tz, day: 'numeric' }).format(apptDate)
                    const monthAbbr    = new Intl.DateTimeFormat('es-MX', { timeZone: tz, month: 'short' })
                                          .format(apptDate).replace('.', '').toUpperCase()
                    const timeStr      = formatClinicTime(appointment.appointment_date, tz)
                    const typeLabel    = appointmentTypeLabels[appointment.appointment_type] ?? appointment.appointment_type

                    return (
                      <div
                        key={appointment.id}
                        className="flex items-center gap-4 bg-white border border-ml-outline-variant/15 rounded-2xl px-5 py-4 shadow-sm hover:shadow-md hover:border-ml-primary/20 transition-all"
                      >
                        {/* Date ticket — left block */}
                        <div className="flex-shrink-0 w-[62px] flex flex-col items-center justify-center bg-ml-surface-container-low/80 rounded-xl py-3 px-1 gap-0">
                          <span className="text-[9px] font-bold text-ml-on-surface-variant/45 uppercase tracking-widest leading-none">
                            {monthAbbr}
                          </span>
                          <span className="text-[24px] font-headline font-bold text-ml-primary leading-tight">
                            {dayNum}
                          </span>
                          <span className="text-[9px] font-semibold text-ml-on-surface-variant/60 leading-none mt-0.5 tabular-nums">
                            {timeStr}
                          </span>
                        </div>

                        {/* Patient name + reason + doctor */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 mb-0.5">
                            <span className="font-bold text-base text-ml-on-surface leading-snug truncate">
                              {patientName}
                            </span>
                            <span className="text-[10px] font-mono text-ml-on-surface-variant/35 flex-shrink-0 leading-none">
                              #{appointment.appointment_number?.slice(-6) ?? '---'}
                            </span>
                          </div>
                          <p className="text-sm text-ml-on-surface-variant/80 truncate leading-snug">
                            {appointment.reason || typeLabel}
                            {appointment.duration_minutes ? ` · ${appointment.duration_minutes} min` : ''}
                          </p>
                          <p className="text-[11px] text-ml-on-surface-variant/45 mt-0.5 truncate">
                            {doctorName} · {typeLabel}
                          </p>
                        </div>

                        {/* Status pill */}
                        <div className="flex-shrink-0">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-semibold border',
                              sc.bg, sc.border, sc.text,
                            )}
                          >
                            <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', sc.dot)} />
                            {sc.label}
                          </span>
                        </div>

                        {/* Quick action icons */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-ml-surface-container-low transition-colors text-ml-on-surface-variant/50 hover:text-ml-primary"
                                onClick={() => navigate(`/workspace/patient/${appointment.patient_id}/historial-citas`)}
                                aria-label="Ver expediente del paciente"
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Ver expediente</TooltipContent>
                          </Tooltip>

                          {canChangeStatus ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-ml-surface-container-low transition-colors text-ml-on-surface-variant/50 hover:text-ml-on-surface"
                                  aria-label="Más acciones"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="min-w-[180px]">
                                {nextStatuses.length > 0 && (
                                  <>
                                    <DropdownMenuLabel className="text-xs font-semibold text-ml-on-surface-variant/60">
                                      Cambiar estado
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {nextStatuses.map((next) => (
                                      <DropdownMenuItem
                                        key={next}
                                        onClick={() => handleStatusChange(appointment, next)}
                                      >
                                        <span
                                          className={cn(
                                            'w-2 h-2 rounded-full mr-2.5 flex-shrink-0',
                                            statusConfig[next]?.dot ?? 'bg-gray-400',
                                          )}
                                        />
                                        {statusConfig[next]?.label ?? next}
                                      </DropdownMenuItem>
                                    ))}
                                  </>
                                )}
                                {isDeletable && (
                                  <>
                                    {nextStatuses.length > 0 && <DropdownMenuSeparator />}
                                    <DropdownMenuItem
                                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                      onClick={() => handleDelete(appointment)}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Eliminar cita
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {nextStatuses.length === 0 && !isDeletable && (
                                  <DropdownMenuItem disabled className="text-xs">
                                    Sin acciones disponibles
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-ml-surface-container-low transition-colors text-ml-on-surface-variant/50">
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && total > pageSize && (
          <div className="mt-6 flex items-center justify-between">
            <p className="text-[10px] font-bold text-ml-on-surface-variant/40 uppercase tracking-widest">
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} de {total} citas
            </p>
            <div className="flex items-center gap-1.5">
              <button
                className="w-8 h-8 flex items-center justify-center border border-ml-outline-variant/30 rounded-lg text-ml-primary hover:bg-ml-surface-container-low transition-colors disabled:opacity-40"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                aria-label="Página anterior"
              >
                <ChevronRight className="h-4 w-4 rotate-180" />
              </button>
              <span className="w-8 h-8 flex items-center justify-center bg-ml-primary text-white text-[11px] font-bold rounded-lg">
                {page}
              </span>
              <button
                className="w-8 h-8 flex items-center justify-center border border-ml-outline-variant/30 rounded-lg text-ml-primary hover:bg-ml-surface-container-low transition-colors disabled:opacity-40"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                aria-label="Página siguiente"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <CreateAppointmentDialog
          open={isCreateOpen}
          onOpenChange={setIsCreateOpen}
          onSuccess={refresh}
        />
      </div>
    </TooltipProvider>
  )
}
